import React from "react";
import { Link } from "react-router-dom";
import { AiArtwork } from "../data/aiArtworks";
import { useAiArtwork } from "../hooks/useAiArtwork";
import { artworkPagePath } from "../lib/artworkPaths";
import { ArtworkImage } from "./ArtworkImage";
import { ArtworkLightboxActions } from "./ArtworkLightboxActions";

export function AiGalleryCard({
  work,
  onClick,
  to,
  style,
  className = "mb-5",
}: {
  work: AiArtwork;
  onClick?: () => void;
  to?: string;
  style?: React.CSSProperties;
  className?: string;
}) {
  const translated = useAiArtwork(work);
  const cardClass = `group art-card block w-full break-inside-avoid text-start animate-fade-in ${className}`;

  const content = (
    <>
      <ArtworkImage
        src={translated.imageUrl}
        alt={translated.name}
        layout="masonry"
        imgClassName="transition-transform duration-700 group-hover:scale-[1.03]"
      />
      <div className="p-5">
        <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-accent">
          {translated.collectionLabel}
        </p>
        <h3 className="mt-1 font-display text-xl font-medium text-canvas-950">
          {translated.name}
        </h3>
        {translated.description && (
          <p className="mt-2 line-clamp-2 text-sm text-canvas-600">
            {translated.description}
          </p>
        )}
      </div>
    </>
  );

  if (to) {
    return (
      <Link to={to} className={cardClass} style={style}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${cardClass} border-0 p-0`}
      style={style}
    >
      {content}
    </button>
  );
}

export function FeaturedAiCard({
  work,
  onClick,
}: {
  work: AiArtwork;
  onClick?: () => void;
}) {
  const translated = useAiArtwork(work);
  const inner = (
    <>
      <ArtworkImage
        src={translated.imageUrl}
        alt={translated.name}
        aspectClass="aspect-square"
        imgClassName="transition-transform duration-500 group-hover:scale-105"
      />
      <div className="p-3 sm:p-4">
        <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-accent">
          {translated.collectionLabel}
        </p>
        <h3 className="mt-1 font-display text-base font-medium text-canvas-950 line-clamp-2 sm:text-lg">
          {translated.name}
        </h3>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group art-card w-full overflow-hidden border-0 p-0 text-start"
      >
        {inner}
      </button>
    );
  }

  return (
    <Link to="/gallery?tab=ai" className="group art-card block overflow-hidden">
      {inner}
    </Link>
  );
}

export function AiLightboxContent({
  work,
  exploreGalleryHref,
}: {
  work: AiArtwork;
  exploreGalleryHref?: string;
}) {
  const translated = useAiArtwork(work);
  return (
    <>
      <p className="text-xs font-medium uppercase tracking-[0.15em] text-accent">
        {translated.collectionLabel}
      </p>
      <h2 className="mt-2 font-display text-xl font-semibold text-canvas-950 sm:text-2xl md:text-3xl">
        {translated.title}
      </h2>
      {translated.description && (
        <p className="mt-3 text-sm leading-relaxed text-canvas-600 sm:mt-4">
          {translated.description}
        </p>
      )}
      <ArtworkLightboxActions
        artworkPageHref={artworkPagePath("ai", work.id)}
        exploreGalleryHref={exploreGalleryHref}
        share={{
          medium: "ai",
          id: work.id,
          title: translated.title,
          text: translated.description,
        }}
        inquire={{
          medium: "ai",
          id: work.id,
          name: translated.name,
        }}
      />
    </>
  );
}
